import Vue from '../core/Vue';
import Vuex from 'vuex';
import {refreshSession, acceptSession, securityState, securityRequest} from '@/utils/sessionSecurity';
import ajax from '../utils/ajax';
import jwt_decode from "jwt-decode";
import Materials from './Materials';
import TasksManagement from './TasksManagement'
import Requests from './Requests';
import requestCard from './Requests/requestCard';
import Reports from './Reports';
import DeleteShiftReport from './Requests/DeleteShiftReport';
import FuelCards from './FuelCards';
import MechanicAssets from './MechanicAssets';
import ObjectCards from './ObjectCards';
import ObjectCreation from './ObjectCreation';
import AdminPanel from './AdminPanel'
import ShiftReport from './ShiftReport';
import Lifecycle from './Lifecycle';
import AppVersion from './AppVersion';
import Rules from "@/utils/Abilities/rules";
import {AbilityBuilder} from "@casl/ability";
import {uploadFileWithRetry} from '@/utils/fileUploadQueue';
import {exchangeInstallBootstrapSession} from '@/utils/appInstallCampaignApi';
import {
    createInstallRequestId,
    getBrowserInstallDeviceId,
    getInstallPlatform
} from '@/utils/appInstallation';

Vue.use(Vuex);

function getCompanyFromQuery() {
    const company = new URLSearchParams(window.location.search).get(`company`);
    if (!company || company === `undefined` || company === `null` || company === `{{company}}`) {
        return undefined;
    }
    return company;
}

function isStandaloneInstallLaunch() {
    return Boolean(
        (
            window.matchMedia
            && (
                window.matchMedia('(display-mode: standalone)').matches
                || window.matchMedia('(display-mode: fullscreen)').matches
            )
        )
        || navigator.standalone === true
        || document.referrer.indexOf('android-app://') === 0
    );
}

const INSTALL_BOOTSTRAP_REQUEST_KEY =
    'argus.appInstallation.bootstrapRequestId.v1';

function getInstallBootstrapRequestId() {
    try {
        let requestId = window.localStorage.getItem(
            INSTALL_BOOTSTRAP_REQUEST_KEY
        );
        if (!requestId) {
            requestId = createInstallRequestId();
            window.localStorage.setItem(
                INSTALL_BOOTSTRAP_REQUEST_KEY,
                requestId
            );
        }
        return requestId;
    } catch (error) {
        return createInstallRequestId();
    }
}

function clearInstallBootstrapRequestId() {
    try {
        window.localStorage.removeItem(INSTALL_BOOTSTRAP_REQUEST_KEY);
    } catch (error) {
        // Следующий запуск создаст новый идентификатор запроса.
    }
}

export default new Vuex.Store({
    modules: {
        materials: Materials,
        tasksManagement: TasksManagement,
        requests: Requests,
        requestCard,
        reports: Reports,
        deleteShiftReport: DeleteShiftReport,
        fuelCards: FuelCards,
        mechanicAssets: MechanicAssets,
        objectCards: ObjectCards,
        objectCreation: ObjectCreation,
        adminPanel: AdminPanel,
        shiftReport: ShiftReport,
        lifecycle: Lifecycle,
        appVersion: AppVersion
    },
    actions: {
        async initializeApp({commit, getters, dispatch, state}) {
            dispatch('appVersion/initialize');                      
            if (state.refreshToken && state.token) {
                try {
                    const restored = await refreshSession('staff');
                    commit('setToken', restored.accessToken); commit('setRefreshToken', 'cookie');
                    dispatch('setAuthorizationHeader', restored.accessToken);
                    commit('setAppInitialized', true); return;
                } catch (error) {
                    if (![401, 428].includes(error?.response?.status)) {
                        dispatch('setAuthorizationHeader', state.token);
                        commit('setAppInitialized', true); return;
                    }
                    commit('removeToken'); commit('removeRefreshToken');
                    securityState.locked = false; securityState.identity = null;
                }
            }
            if (getters.isTelegramWebApps) {
                const payload = {initData: global?.Telegram?.WebApp?.initData};
                const company = getCompanyFromQuery();
                commit(`removeToken`);
                commit(`removeRefreshToken`);
                dispatch('setAuthorizationHeader', null);
                try {
                    const result = await dispatch('login', {payload, company});
                    if (result.accessToken) {
                        acceptSession('staff', result, true);
                        commit('setToken', result.accessToken); commit('setRefreshToken', 'cookie');
                        dispatch('setAuthorizationHeader', result.accessToken);
                    } else securityState.pendingLogin = {result, payload};
                } catch (_) { /* A fresh widget/password login remains available. */ }
                commit(`setAppInitialized`, true);
                return Promise.resolve();
            } else {
                if (
                    isStandaloneInstallLaunch()
                    && (!getters.isAuthorized || !state.refreshToken)
                ) {
                    const requestId = getInstallBootstrapRequestId();
                    try {
                        const session = await exchangeInstallBootstrapSession({
                            browserDeviceId: getBrowserInstallDeviceId(),
                            platform: getInstallPlatform(),
                            requestId
                        });
                        if (
                            session
                            && session.accessToken
                            && session.refreshToken
                        ) {
                            commit(`setToken`, session.accessToken);
                            commit(`setRefreshToken`, session.refreshToken);
                            dispatch(`setAuthorizationHeader`, session.accessToken);
                            clearInstallBootstrapRequestId();
                        }
                    } catch (error) {
                        const status = error && error.response && error.response.status;
                        if (status === 400 || status === 410) {
                            clearInstallBootstrapRequestId();
                        }
                        // Одноразовый bootstrap отсутствует или истёк:
                        // обычный переход на страницу входа остаётся рабочим.
                    }
                }
                commit(`setAppInitialized`, true);
                return Promise.resolve();
            }
        },
        changeUserActive(_, {id, active}) {
            return ajax.changeUserActive(id, {active});
        },
        getUserStatusImpact(_, id) {
            return ajax.getUserStatusImpact(id);
        },
        getUserDeletionImpact(_, id) {
            return ajax.getUserDeletionImpact(id);
        },
        updateAbility({getters}, ctx) {
            const {rules} = Rules(new AbilityBuilder(), getters.currentUser);
            ctx.$ability.update(rules);
        },
        abortWizard({dispatch, commit}) {
            // Authentication changes clear memory only. Drafts and objects are never deleted here.
            commit('objectCreation/reset');
            dispatch(`clearWizard`);
        },
        clearWizard({state}) {
            state.wizard.selectedCurator = null;
            state.wizard.chatId = null;
            state.wizard.createdObject = null;
            state.wizard.folder = null;
            state.wizard.selectedObject = null;
            state.wizard.selectedStaff = null;
            state.wizard.targets = [];
            state.wizard.time = {
                HH: '09',
                mm: '00'
            };
            state.wizard.contract = {
                name: null,
                date: null
            }
        },
        async changeActivityObjectInProcess(_, {building_process_id, value}) {
            return await ajax.changeActivityObjectInProcess(building_process_id, value).then(res => res.data);
        },
        async changeNotificationObjectInProcess(_, {building_process_id, value}) {
            return await ajax.changeNotificationObjectInProcess(building_process_id, value).then(res => res.data);
        },
        async createObject(_, data) {
            return await ajax.createObject(data);
        },
        async createObjectsInProcess(_, data) {
            return await ajax.createObjectsInProcess(data);
        },
        async createSystemUser(_, user) {
            const userId = user.user_id;
            await ajax.createSystemUser(user).then(async () => {
                for (const role of user.roles) {
                    await ajax.addRoleToUser(userId, role);
                }
            })
        },
        async createTarget(_, data) {
            return await ajax.createTarget(data);
        },
        createUserGroup(_, payload) {
            return ajax.createUserGroup(payload);
        },
        createTelegramGroup(_, payload) {
            return ajax.createTelegramGroup(payload);
        },
        addUsersToTelegram(_, {id, payload}) {
            return ajax.addUsersToTelegram(id, payload);
        },
        async createWorkType(_, data) {
            return await ajax.createWorkType(data);
        },
        async createWorkGroup(_, data) {
            return await ajax.createWorkGroup(data);
        },
        async createUser(_, user) {
            return await ajax.createUser(user).then(res => res.data);
        },
        async getEmployeeDirectorySettings({commit}) {
            const settings = await ajax.getEmployeeDirectorySettings();
            commit('setEmployeeDirectorySettings', settings);
            return settings;
        },
        async updateEmployeeDirectorySettings({commit}, payload) {
            const settings = await ajax.updateEmployeeDirectorySettings(payload);
            commit('setEmployeeDirectorySettings', settings);
            return settings;
        },
        async getEmploymentOrganizations(_, includeInactive = false) {
            return ajax.getEmploymentOrganizations(includeInactive);
        },
        async createEmploymentOrganization(_, payload) {
            return ajax.createEmploymentOrganization(payload);
        },
        async previewGoogleContactsImport(_, file) {
            return ajax.previewGoogleContactsImport(file);
        },
        async applyGoogleContactsImport(_, payload) {
            return ajax.applyGoogleContactsImport(payload);
        },
        async exportGoogleContacts(_, params) {
            return ajax.exportGoogleContacts(params || {});
        },
        async exportEmployeeGoogleContact(_, userId) {
            return ajax.exportEmployeeGoogleContact(userId);
        },
        async getMyProfile() {
            return ajax.getMyProfile();
        },
        async navigation_loadSettings({commit, getters}, options = {}) {
            if (!getters.isAuthorized) {
                commit('navigation_reset');
                return null;
            }
            commit('navigation_setLoading', true);
            try {
                const settings = await ajax.getNavigationSettings();
                commit('navigation_setSettings', settings || {});
                return settings;
            } catch (error) {
                commit('navigation_setLoaded');
                if (!options || options.silent !== true) {
                    throw error;
                }
                return null;
            } finally {
                commit('navigation_setLoading', false);
            }
        },
        async navigation_updatePreferredWorkspace({commit}, workspaceKey) {
            const settings = await ajax.updatePreferredWorkspace(workspaceKey);
            commit('navigation_setSettings', settings || {});
            return settings;
        },
        async navigation_loadAttention({commit, dispatch}) {
            const results = await Promise.all([
                ajax.getTaskAttentionCount().catch(() => ({
                    total: 0,
                    overdue: 0,
                    upcoming: 0,
                    control_due: 0
                })),
                dispatch('requests_getCounts').catch(() => ({
                    requires_action: 0
                }))
            ]);
            const payload = {
                tasks: results[0] || {},
                requests: Number(results[1] && results[1].requires_action) || 0
            };
            commit('navigation_setAttention', payload);
            return payload;
        },
        async navigation_clearShiftReportDrafts(_, drafts) {
            return ajax.clearHomeShiftReportDrafts(drafts);
        },
        async navigation_loadHomeSummary({commit}) {
            try {
                const summary = await ajax.getHomeSummary();
                commit('navigation_setHomeSummary', summary || {});
                return summary;
            } catch (error) {
                commit('navigation_setHomeSummary', {});
                return {};
            }
        },
        async updateMyProfile(_, payload) {
            return ajax.updateMyProfile(payload);
        },
        async getEmployeeCard(_, userId) {
            return ajax.getEmployeeCard(userId);
        },
        async getEmployeeContactCard(_, userId) {
            return ajax.getEmployeeContactCard(userId);
        },
        async uploadUserAvatar(_, {userId, file}) {
            return uploadFileWithRetry(() => ajax.uploadUserAvatar(userId, file));
        },
        async deleteUserAvatar(_, userId) {
            return ajax.deleteUserAvatar(userId);
        },
        async deleteSystemUser(_, user) {
            return new Promise(async (resolve, reject) => {
                user.login = null;
                user.password = null;
                user.user_id = user.userId;
                await ajax.deleteSystemUser(user.userId).catch((err) => reject(err));
                await ajax.deleteRolesByUser(user.userId).catch((err) => reject(err));
                resolve();
            })
        },
        async deleteObject(_, objectId) {
            return await ajax.deleteObject(objectId);
        },
        async deleteObjectInProcess(_, processId) {
            return await ajax.deleteObjectInProcess(processId);
        },
        async deleteRolesByUserId(_, userId) {
            return ajax.deleteRolesByUserId(userId);
        },
        async deleteTarget(_, data) {
            return await ajax.deleteTarget(data);
        },
        async deleteUser(_, {userId, acknowledgedIrreversible = false}) {
            return await ajax.deleteUser(userId, {
                acknowledged_irreversible: acknowledgedIrreversible
            });
        },
        deleteUserGroup(_, id) {
            return ajax.deleteUserGroup(id);
        },
        async deleteWorkType(_, workId) {
            return await ajax.deleteWorkType(workId);
        },
        async setWorkTypeStatus(_, {workId, isActive}) {
            return await ajax.setWorkTypeStatus(workId, isActive);
        },
        async editObject(_, data) {
            const {objectId} = data;
            return await ajax.editObject(objectId, data);
        },
        async editObjectInProcess(_, data) {
            const {building_process_id} = data;
            return await ajax.editObjectInProcess(building_process_id, data);
        },
        async editTarget(_, data) {
            return await ajax.editTarget(data);
        },
        async editUser(_, data) {
            const {userId} = data;
            return await ajax.updateUser(userId, data);
        },
        editUserGroup(_, {groupId, payload}) {
            return ajax.editUserGroup(groupId, payload);
        },
        async editSystemUser(_, data) {
            const {user_id} = data;
            return await ajax.editSystemUser(user_id, data).then(async () => {
                await ajax.deleteRolesByUserId(user_id);
                for (const role of data.roles) {
                    await ajax.addRoleToUser(user_id, role);
                }
            });
        },
        async editWorkType(_, data) {
            const {work_id} = data;
            return await ajax.editWorkType(work_id, data);
        },
        async editWorkGroup(_, {id, data}) {
            return await ajax.editWorkGroup(id, data);
        },
        async getCompletedWorks({commit}) {
            await ajax.getCompletedWorks().then(data => commit(`setCompletedWorks`, data));
        },
        async getCompletedWorksMainAndSeconds({commit}, archive = false) {
            const data = await ajax.getCompletedWorksWithSecondWorks(Boolean(archive));
            commit(`setCompletedWorksMainAndSeconds`, data);
            return data;
        },
        async createFuelLimit({getters, dispatch}, payload) {
            const author_id = getters.currentUser ? getters.currentUser.userId : null;
            await ajax.createFuelLimit(Object.assign({}, payload, {author_id}));
            await dispatch(`getFuelLimits`);
        },
        async updateFuelLimit({getters, dispatch}, payload) {
            const author_id = getters.currentUser ? getters.currentUser.userId : null;
            await ajax.updateFuelLimit(Object.assign({}, payload, {author_id}));
            await dispatch(`getFuelLimits`);
        },
        async deleteFuelLimitsByProcess({getters, dispatch}, {building_process_id, fuel_id}) {
            const author_id = getters.currentUser ? getters.currentUser.userId : null;
            await ajax.deleteFuelLimits(building_process_id, fuel_id, author_id);
            await dispatch(`getFuelLimits`);
        },
        getUserGroups() {
            return ajax.getUserGroups();
        },
        async getFuelLimits({commit}, archive = false) {
            await ajax.getFuelLimits(Boolean(archive))
                .then(data => commit(`setFuelLimits`, Array.isArray(data) ? data : []));
        },
        async getTypesOfFuel({commit}) {
            const data = await ajax.getTypesOfFuel();
            if (Array.isArray(data)) {
                commit(`setFuelTypes`, data);
            }
        },
        async getImages(_, {limit, skip, query_building, shifts_limit, media_limit, report_status, work_ids, work_group_ids, cancelToken}) {
            return await ajax.getImages(limit, skip, query_building, {shifts_limit, media_limit, report_status, work_ids, work_group_ids, cancelToken});
        },
        async getImagesForBuilding(_, {id, limit, skip, query_building, search_scope, media_limit, report_status, work_ids, work_group_ids, cancelToken}) {
            return await ajax.getImagesForBuilding(id, limit, skip, {
                cancelToken,
                query_building,
                search_scope,
                media_limit,
                report_status,
                work_ids,
                work_group_ids
            })
        },
        async getGalleryAccessSettings() {
            return await ajax.getGalleryAccessSettings();
        },
        async updateGalleryAccessSettings(_, accessConfig) {
            return await ajax.updateGalleryAccessSettings(accessConfig);
        },
        async getGalleryObjectAccess() {
            return await ajax.getGalleryObjectAccess();
        },
        async updateGalleryObjectAccess(_, {id, accessConfig}) {
            return await ajax.updateGalleryObjectAccess(id, accessConfig);
        },
        async getImage(_, id) {
            return await ajax.getImage(id);
        },
        async getMiniImage(_, id) {
            return await ajax.getMiniImage(id);
        },
        async getObjects({commit}) {
            await ajax.getObjects().then(data => commit(`setObjects`, data));
        },
        async getObjectsInProcess({commit}, archive = false) {
            const options = archive && typeof archive === 'object' ? archive : {archive};
            await ajax.getObjectsInProcess(Boolean(options.archive), options)
                .then(data => commit(`setObjectsInProcess`, Array.isArray(data) ? data : []));
        },
        async getSecondWorks({commit}) {
            await ajax.getSecondWorks().then(data => commit(`setSecondWorks`, data));
        },
        async getRoles({commit}) {
            commit(`setRoles`, await ajax.getRoles());
        },
        async getSystemUsers({commit}) {
            await ajax.getSystemUsers().then(users => {
                commit(`setSystemUsers`, users);
            })
        },
        async getSystemUser(_, userId) {
            return await ajax.getSystemUser(userId).then(res => res.data[0]);
        },
        async getTargets({commit}, archive = false) {
            await ajax.getTargets(Boolean(archive)).then(data => commit(`setTargets`, data));
        },
        login({commit, dispatch}, {payload, company}) {
            return securityRequest('staff', 'login' + (company ? '?company=' + encodeURIComponent(company) : ''), payload);
        },
        setAuthorizationHeader(_, token) {
            ajax.setHeaderAuth(token);
        },
        async refreshToken({state}, {vm}) {
            const {refreshToken} = state;
            if (!refreshToken) {
                return Promise.resolve({accessToken: null});
            } else {
                const result = await refreshSession('staff');
                // Logout or another login invalidates an in-flight refresh.
                if (state.refreshToken !== refreshToken) return {discarded: true};
                return result;
            }
        },
        async refreshAuthorization({dispatch, commit, state}, {vm} = {}) {
            if (!vm) {
                return false;
            }
            const session = state.refreshToken;
            const {accessToken, refreshToken} = await dispatch(`refreshToken`, {vm});
            if (state.refreshToken !== session) return false;
            if (!accessToken || !refreshToken) {
                return false;
            }

            commit(`setToken`, accessToken);
            commit(`setRefreshToken`, refreshToken);
            dispatch(`setAuthorizationHeader`, accessToken);
            dispatch(`updateAbility`, vm);
            return true;
        },
        async getUsedFuels({commit, dispatch}, archive = false) {
            await ajax.getUsedFuels(Boolean(archive))
                .then(data => commit(`setUsedFuels`, data))
                .catch(res => dispatch(`showErrorModal`, res.response.data));
        },
        async getUsers({commit}) {
            const users = await ajax.getShortUsers();
            commit(`setUsers`, users);
            return users;
        },
        async getAccountUsers({commit}, data) {
            commit(`setUsers`, await ajax.getUsers(data?.full));
        },
        async getUser(_, userId) {
            return ajax.getUser(userId);
        },
        async getUserHistory(_, userId) {
            return ajax.getUserHistory(userId);
        },
        async getDepartments({commit}, options = false) {
            const {all, ignoreModal} = typeof options === 'object' && options ? options : {all: options};
            const departments = await ajax.getDepartments(all, {ignoreModal});
            commit(`setDepartments`, departments);
            return departments;
        },
        async createDepartment(_, payload) {
            return ajax.createDepartment(payload);
        },
        async updateDepartment(_, {id, payload}) {
            return ajax.updateDepartment(id, payload);
        },
        async deleteDepartment(_, id) {
            return ajax.deleteDepartment(id);
        },
        async getDepartment(_, id) {
            return ajax.getDepartment(id);
        },
        async getWorkShifts({commit}, archive = false) {
            await ajax.getWorkShifts(Boolean(archive)).then(data => {
                commit(`setWorkShifts`, data);
            });
        },
        async getWorkTypes({commit}) {
            await ajax.getWorkTypes().then(data => commit(`setWorkTypes`, data));
        },
        async getWorkGroups({commit}) {
            await ajax.getWorkGroups().then(data => commit(`setWorkGroups`, data));
        },
        hideErrorModal({commit}) {
            commit(`setErrorModalText`, null);
            commit(`setShowErrorModal`, false);
        },
        async restartNotifications() {
            await ajax.restartNotifications();
        },
        showErrorModal({commit, state}, text) {
            if (state.appVersion.isUpdateRequired || state.appVersion.connectionLost) return;
            commit(`setErrorModalText`, text);
            commit(`setShowErrorModal`, true);
        },
        getAvailableReportsManagement() {
            return ajax.getAvailableReportsManagement();
        },
        getActiveReports() {
            return ajax.getActiveReports();
        },
        fillReportsTableByCurrentProcesses(_, reportId) {
            return ajax.fillReportsTableByCurrentProcesses(reportId);
        },
        deleteActiveReport(_, id) {
            return ajax.deleteActiveReport(id);
        },
        deleteActiveReportByProcess(_, id) {
            return ajax.deleteActiveReportByProcess(id);
        },
        runReportsForcibly() {
            return ajax.runReportsForcibly();
        },
        createReportManagement(_, building_process_id) {
            const payload = {
                building_process_id,
                reportId: 1
            }
            return ajax.createReportManagement(payload)
        },
        getAvailableAlertsManagement() {
            return ajax.getAvailableAlertsManagement();
        },
        getActiveAlerts() {
            return ajax.getActiveAlerts();
        },
        fillAlertsTableByCurrentProcesses(_, alertId = 0) {
            return ajax.fillAlertsTableByCurrentProcesses(alertId);
        },
        deleteActiveAlert(_, id) {
            return ajax.deleteActiveAlert(id);
        },
        deleteActiveAlertByProcess(_, id) {
            return ajax.deleteActiveAlertByProcess(id);
        },
        createAlertManagement(_, building_process_id) {
            const payload = {
                building_process_id,
                alertId: 0
            }
            return ajax.createAlertManagement(payload)
        },
        sendDatePicker(_, payload) {
            return ajax.sendDatePicker(payload);
        },
        onScreenResize({commit}) {
            commit(`setIsMobile`, window.innerWidth < 576);
        },
        checkLoginAvailable(_, login) {
            return ajax.checkLoginAvailable(login);
        }
    },
    getters: {
        isAdmin({token}) {
            if (token) {
                const {roles} = jwt_decode(token);
                return roles.includes(`Admin`)
            } else {
                return false;
            }
        },
        isAuthorized({token}) {
            // Expiration locks the mounted workspace; it must not destroy unsaved forms.
            try { const value = jwt_decode(token); return value.iss === 'argus-session' && Boolean(value.sid) && value.aud === 'staff'; }
            catch (_) { return false; }
        },
        currentUser({token}) {
            if (token) {
                const {firstName, lastName, userId, login, phone, roles, company, companyName, abilities} = jwt_decode(token);
                return {
                    firstName,
                    lastName,
                    userId,
                    login,
                    phone,
                    roles,
                    company,
                    companyName,
                    abilities
                }
            } else {
                return null;
            }
        },
        isTelegramWebApps() {
            if (global.Telegram) {
                const {Telegram: {WebApp: {initDataUnsafe}}} = global;
                return Boolean(Object.keys(initDataUnsafe).length) && initDataUnsafe.constructor === Object;
            } else {
                return false;
            }
        },
        isMobile() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        },
        selection() {
            return global.getSelection()?.toString();
        },
        isAds(state, getters) {
            return getters.currentUser ? [`aerodorstroy`, `aerodorstroy_ru`].includes(getters.currentUser.company) : false;
        },
        navigationReady({navigationExperience}) {
            return Boolean(
                navigationExperience &&
                navigationExperience.loaded
            );
        }
    },
    state: {
        appInitialized: false,
        completedWorks: [],
        completedWorksMainAndSeconds: [],
        errorModalText: null,
        fuelLimits: [],
        fuelTypes: [],
        images: [
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
            `/images/1`,
        ],
        objects: [],
        objectsInProcess: [],
        refreshToken: localStorage.getItem('refreshToken'),
        token: localStorage.getItem('token') || '',
        roles: [],
        secondWorks: [],
        showOnlyArchive: false,
        systemUsers: [],
        showErrorModal: false,
        targets: [],
        usedFuels: [],
        user: {},
        users: [],
        departments: [],
        employeeDirectorySettings: null,
        navigationExperience: {
            loaded: false,
            loading: false,
            ui_version: 'modern',
            preferred_workspace_key: null,
            department_default_workspace_keys: [],
            attention: {
                tasks: {
                    total: 0,
                    overdue: 0,
                    upcoming: 0,
                    control_due: 0
                },
                requests: 0
            },
            home: {}
        },
        selectedDepartmentId: null,
        workShifts: [],
        workTypes: [],
        workGroups: [],
        wizard: {
            selectedCurator: null,
            chatId: null,
            createdObject: null,
            folder: null,
            selectedObject: null,
            selectedStaff: null,
            targets: [],
            time: {
                HH: '09',
                mm: '00'
            },
            contract: {
                name: null,
                date: null
            }
        },
        isMobile: window.innerWidth < 576,
        onProcess: false
    },
    mutations: {
        setAppInitialized(state, value) {
            state.appInitialized = value;
        },
        removeRefreshToken(state) {
            localStorage.removeItem('refreshToken');
            state.refreshToken = null;
        },
        removeToken(state) {
            localStorage.removeItem('token');
            state.token = null;
            document.cookie = `token=; path=/; Max-Age=0`;
            state.navigationExperience = {
                loaded: false,
                loading: false,
                ui_version: 'modern',
                preferred_workspace_key: null,
                department_default_workspace_keys: [],
                attention: {
                    tasks: {
                        total: 0,
                        overdue: 0,
                        upcoming: 0,
                        control_due: 0
                    },
                    requests: 0
                },
                home: {}
            };
        },
        setCompletedWorks(state, value) {
            state.completedWorks = value;
        },
        setCompletedWorksMainAndSeconds(state, value) {
            state.completedWorksMainAndSeconds = value;
        },
        setErrorModalText(state, value) {
            state.errorModalText = value;
        },
        setFuelLimits(state, value) {
            state.fuelLimits = value;
        },
        setFuelTypes(state, value) {
            state.fuelTypes = value;
        },
        setImages(state, value) {
            state.images = value;
        },
        setObjects(state, value) {
            state.objects = value;
        },
        setObjectsInProcess(state, value) {
            state.objectsInProcess = value;
        },
        setSecondWorks(state, value) {
            state.secondWorks = value;
        },
        setShowOnlyArchive(state, value) {
            state.showOnlyArchive = value;
        },
        setSystemUsers(state, value) {
            state.systemUsers = value;
        },
        setShowErrorModal(state, value) {
            state.showErrorModal = value;
        },
        setTargets(state, value) {
            state.targets = value;
        },
        setToken(state, token) {
            localStorage.setItem('token', token);
            state.token = token;
            document.cookie = `token=${token}; path=/; Max-Age=900; SameSite=Strict${location.protocol === 'https:' ? '; Secure' : ''}`;
        },
        setRefreshToken(state, refreshToken) {
            localStorage.setItem('refreshToken', 'cookie');
            state.refreshToken = 'cookie';
        },
        setRoles(state, value) {
            state.roles = value;
        },
        setUsedFuels(state, value) {
            state.usedFuels = value;
        },
        setUsers(state, value) {
            state.users = value;
        },
        setDepartments(state, value) {
            state.departments = value;
        },
        setEmployeeDirectorySettings(state, value) {
            state.employeeDirectorySettings = value;
        },
        navigation_setLoading(state, value) {
            state.navigationExperience.loading = Boolean(value);
        },
        navigation_setLoaded(state) {
            state.navigationExperience.loaded = true;
        },
        navigation_setSettings(state, settings) {
            const current = state.navigationExperience;
            state.navigationExperience = {
                ...current,
                loaded: true,
                ui_version: 'modern',
                preferred_workspace_key: settings.preferred_workspace_key || null,
                department_default_workspace_keys: Array.isArray(settings.department_default_workspace_keys)
                    ? settings.department_default_workspace_keys.filter(Boolean)
                    : []
            };
        },
        navigation_setAttention(state, attention) {
            state.navigationExperience.attention = {
                tasks: {
                    ...state.navigationExperience.attention.tasks,
                    ...((attention && attention.tasks) || {})
                },
                requests: Number(attention && attention.requests) || 0
            };
        },
        navigation_setHomeSummary(state, summary) {
            state.navigationExperience.home = summary || {};
        },
        navigation_reset(state) {
            state.navigationExperience = {
                loaded: true,
                loading: false,
                ui_version: 'modern',
                preferred_workspace_key: null,
                department_default_workspace_keys: [],
                attention: {
                    tasks: {
                        total: 0,
                        overdue: 0,
                        upcoming: 0,
                        control_due: 0
                    },
                    requests: 0
                },
                home: {}
            };
        },
        setSelectedDepartmentId(state, value) {
            state.selectedDepartmentId = value;
        },
        setWorkShifts(state, value) {
            state.workShifts = value;
        },
        setWorkTypes(state, value) {
            state.workTypes = value;
        },
        setWorkGroups(state, value) {
            state.workGroups = value;
        },
        setWizardCreatedObject(state, value) {
            state.wizard.createdObject = value;
        },
        setWizardSelectedObject(state, value) {
            state.wizard.selectedObject = value;
        },
        setWizardSelectedStaff(state, value) {
            state.wizard.selectedStaff = value;
        },
        setWizardSelectedCurator(state, value) {
            state.wizard.selectedCurator = value;
        },
        setWizardFolder(state, value) {
            state.wizard.folder = value;
        },
        setWizardTime(state, value) {
            state.wizard.time = value;
        },
        setWizardChatId(state, value) {
            state.wizard.chatId = value;
        },
        setWizardTargets(state, value) {
            state.wizard.targets = value;
        },
        setWizardContractName(state, value) {
            state.wizard.contract.name = value;
        },
        setWizardContractDate(state, value) {
            state.wizard.contract.date = value;
        },
        setIsMobile(state, value) {
            state.isMobile = value
        },
        setOnProcess(state, value) {
            state.onProcess = value;
        }
    }
})
